import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EventSeatMap, SeatMapBay } from '../../models/event-seat-map.interface';
import {
  LABEL_COLOUR,
  lighten,
  SeatMapTier,
  bayLabel,
  bayTitle,
  formatPrice,
  isPickable,
  paintFor,
  statusText,
  stripActiveContent,
  tiersOf,
} from './seat-map.util';

/**
 * "Find your view": the venue's diagram with each bay coloured by ticket type. The customer filters by
 * ticket type, looks at a bay's price and what is left, and picks a bay; the parent turns that into a
 * ticket-type selection. Colour is never the only signal: every bay also has a text status and a spoken
 * label, and bays can be reached and picked with the keyboard.
 */
@Component({
  selector: 'hypenation-tickets-seat-map-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './seat-map-picker.component.html',
  styleUrl: './seat-map-picker.component.css',
  host: { '[class.picker--branded]': "variant === 'branded'" },
})
export class SeatMapPickerComponent implements OnChanges, AfterViewChecked {
  @Input({ required: true }) seatMap!: EventSeatMap;
  /** The ticket type currently chosen on the page; a bay of another ticket type is no longer "picked". */
  @Input() selectedTicketTypeUid: string | null | undefined;
  /**
   * The ticket types that can be bought right now: the page knows the event's status and each ticket
   * type's, the seat map does not. A bay whose ticket type is not listed is shown, and read out, as closed
   * and cannot be picked. Null means no restriction.
   */
  @Input() pickableTicketTypeUids: string[] | null = null;

  /**
   * 'branded' is the booking app's presentation: the ticket type filter beside the heading, the key under the
   * map with prices, and every bay in an always-open grid (bays that cannot be bought are listed, but cannot be
   * pressed). Behaviour, and what is announced, is the same; 'default' is unchanged.
   */
  @Input() variant: 'default' | 'branded' = 'default';

  /**
   * Show only the bays of this ticket type (the same as pressing its chip), or all of them when null. Lets the page
   * put the choice somewhere else too, such as on its ticket type cards.
   */
  @Input() filterTicketTypeUid: string | null | undefined;

  @Output() bayPicked = new EventEmitter<SeatMapBay>();
  /** The ticket type filter changed because the customer pressed one of this component's chips. */
  @Output() tierChanged = new EventEmitter<string | null>();
  /** The customer is done choosing a bay and wants the next step: how many tickets. */
  @Output() proceed = new EventEmitter<void>();

  @ViewChild('host') host?: ElementRef<HTMLElement>;

  safeSvg: SafeHtml = '';
  /** The API's bays, with any that cannot be bought right now shown as closed. */
  bays: SeatMapBay[] = [];
  /** Bays that can be chosen now: the same ones the map lets you pick, listed for phones and screen readers. */
  pickableBays: SeatMapBay[] = [];
  tiers: SeatMapTier[] = [];
  tierFilter: string | null = null;
  selectedCode: string | null = null;
  activeCode: string | null = null;

  private readonly sanitizer = inject(DomSanitizer);
  private needsPaint = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seatMap']) {
      this.tiers = tiersOf(this.seatMap?.bays ?? []);
      // The API sanitises the SVG; it is cleaned again here, and only then trusted as markup.
      this.safeSvg = this.sanitizer.bypassSecurityTrustHtml(
        stripActiveContent(this.seatMap?.svg ?? '')
      );
      this.tierFilter = null;
      this.selectedCode = null;
      this.activeCode = null;
    }
    if (changes['filterTicketTypeUid']) {
      this.tierFilter = this.filterTicketTypeUid ?? null;
      this.needsPaint = true;
    }
    if (changes['seatMap'] || changes['pickableTicketTypeUids']) {
      const allowed = this.pickableTicketTypeUids;
      this.bays = (this.seatMap?.bays ?? []).map((bay) =>
        allowed !== null &&
        bay.status === 'AVAILABLE' &&
        !allowed.includes(bay.ticketTypeUid ?? '')
          ? { ...bay, status: 'CLOSED' as const }
          : bay
      );
      // a bay that was picked and can no longer be bought is no longer picked
      if (this.selectedCode && !isPickable(this.bayOf(this.selectedCode) ?? ({} as SeatMapBay), null)) {
        this.selectedCode = null;
      }
    }
    this.refreshPickable();
    if (changes['selectedTicketTypeUid'] && this.selectedCode) {
      // the ticket type was changed elsewhere on the page: the picked bay no longer matches it
      const picked = this.bayOf(this.selectedCode);
      if (picked?.ticketTypeUid !== this.selectedTicketTypeUid) this.selectedCode = null;
    }
    this.needsPaint = true;
  }

  ngAfterViewChecked(): void {
    if (this.needsPaint && this.host) {
      this.needsPaint = false;
      this.paint();
    }
  }

  /** the bays in the list: only the ones that can be bought, or (branded) all of them */
  get listBays(): SeatMapBay[] {
    return this.variant === 'branded' ? this.bays : this.pickableBays;
  }

  canPick(bay: SeatMapBay): boolean {
    return isPickable(bay, this.tierFilter);
  }

  /** branded grid: a pale tint of the bay's ticket type colour */
  tileFill(bay: SeatMapBay): string {
    return bay.status === 'AVAILABLE' ? lighten(this.tierColour(bay), 0.82) : '#eef0ea';
  }

  /** branded grid: the line under a bay's number */
  tileLabel(bay: SeatMapBay): string {
    return bay.status === 'AVAILABLE' ? (bay.ticketTypeName ?? '') : this.status(bay);
  }

  get shownBay(): SeatMapBay | undefined {
    return this.bayOf(this.activeCode ?? this.selectedCode);
  }

  get selectedBay(): SeatMapBay | undefined {
    return this.bayOf(this.selectedCode);
  }

  setTier(ticketTypeUid: string | null): void {
    this.tierFilter = ticketTypeUid;
    this.tierChanged.emit(ticketTypeUid);
    this.refreshPickable();
    this.needsPaint = true;
  }

  private refreshPickable(): void {
    this.pickableBays = this.bays.filter((bay) => isPickable(bay, this.tierFilter));
  }

  trackBay(_: number, bay: SeatMapBay): string {
    return bay.code;
  }

  // ---- interaction, delegated from the injected SVG ---------------------------------------------

  onClick(event: Event): void {
    this.choose(this.codeFrom(event));
  }

  onKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const code = this.codeFrom(event);
    if (code) {
      event.preventDefault();
      this.choose(code);
    }
  }

  onHover(event: Event): void {
    const code = this.codeFrom(event);
    if (code !== null && code !== this.activeCode) {
      this.activeCode = code;
    }
  }

  onLeave(): void {
    this.activeCode = null;
  }

  choose(code: string | null): void {
    if (code === null) return;
    // On touch there is no hover: a tap also shows the bay's card, so a bay that cannot be picked
    // still says why.
    this.activeCode = code;
    const bay = this.bayOf(code);
    if (bay && isPickable(bay, this.tierFilter)) {
      this.selectedCode = code;
      this.needsPaint = true;
      this.bayPicked.emit(bay);
    }
  }

  // ---- presentation helpers ---------------------------------------------------------------------

  /** On sale, but the ticket type filter is set to another ticket type: shown quietly and not pickable. */
  filteredOut(bay: SeatMapBay): boolean {
    return bay.status === 'AVAILABLE' && this.tierFilter !== null && bay.ticketTypeUid !== this.tierFilter;
  }

  get filterName(): string {
    return this.tiers.find((t) => t.ticketTypeUid === this.tierFilter)?.name ?? '';
  }

  title(bay: SeatMapBay): string {
    return bayTitle(bay);
  }

  price(bay: SeatMapBay): string {
    return formatPrice(bay.price, bay.currencyCode);
  }

  status(bay: SeatMapBay): string {
    return statusText(bay);
  }

  tierColour(bay: SeatMapBay): string {
    return this.tiers.find((t) => t.ticketTypeUid === bay.ticketTypeUid)?.colour ?? '#d6dbd6';
  }

  tierPrice(tier: SeatMapTier): string {
    return formatPrice(tier.price, tier.currencyCode);
  }

  trackTier(_: number, tier: SeatMapTier): string {
    return tier.ticketTypeUid;
  }

  // ---- painting ---------------------------------------------------------------------------------

  private bayOf(code: string | null): SeatMapBay | undefined {
    return code === null ? undefined : this.bays.find((b) => b.code === code);
  }

  private codeFrom(event: Event): string | null {
    const el = (event.target as Element | null)?.closest?.('[data-bay]');
    return el ? el.getAttribute('data-bay') : null;
  }

  /**
   * Colours every bay from the API's data. The API strips colour from bay shapes, so the page decides
   * how a bay looks from its ticket type and status; text inside a bay keeps a dark label colour.
   */
  private paint(): void {
    const root = this.host?.nativeElement;
    if (!root) return;
    const svg = root.querySelector('svg');
    if (svg) {
      svg.style.width = '100%';
      svg.style.height = 'auto';
      svg.style.maxHeight = '70vh';
      svg.style.display = 'block';
    }
    root.querySelectorAll<SVGElement>('[data-bay]').forEach((el) => {
      const code = el.getAttribute('data-bay') ?? '';
      const bay = this.bayOf(code);
      const paint = paintFor(bay, this.tiers, this.tierFilter);
      const picked = code === this.selectedCode;
      const pickable = !!bay && isPickable(bay, this.tierFilter);

      [el, ...Array.from(el.querySelectorAll<SVGElement>('*'))].forEach((part) => {
        if (part.tagName === 'text' || part.tagName === 'tspan') {
          part.style.fill = paint.textColour;
          part.style.stroke = 'none';
          part.style.opacity = '1';
          part.style.pointerEvents = 'none';
          return;
        }
        part.style.fill = paint.fill;
        part.style.stroke = picked ? LABEL_COLOUR : paint.stroke;
        part.style.strokeWidth = picked ? '3' : '1.5';
        part.style.strokeDasharray = paint.dashed ? '6 4' : '';
        // solid, never translucent: see BayPaint
        part.style.opacity = '1';
      });

      el.style.cursor = pickable ? 'pointer' : 'default';
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', bay ? bayLabel(bay) : `Bay ${code}`);
      el.setAttribute('aria-pressed', `${picked}`);
      if (pickable) {
        el.setAttribute('tabindex', '0');
        el.removeAttribute('aria-disabled');
      } else {
        // reachable for its label, but nothing to choose
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-disabled', 'true');
      }
    });
  }
}
